import React from 'react';
import CityCard from './CityCard';
import DeckSelector from './DeckSelector';
import CategorySelector from './CategorySelector';
import WhichingGameHeader from './WhichingGameHeader';
import WhichingGameOver from './WhichingGameOver';
import ChallengeBanner from './ChallengeBanner';
import { useWhichingGame, WHICHING_STATES } from '../hooks/useWhichingGame';

const WhichingHourGame = ({ onBack, user, onLogout, onViewLeaderboard, onViewStats, onNicknameChange, challengeData, initialDeck, isDailyChallenge = false }) => {
  const game = useWhichingGame({ user, challengeData, initialDeck, isDailyChallenge });
  
  // Category Selection Screen
  if (game.gameState === WHICHING_STATES.CATEGORY_SELECTION) {
    return (
      <CategorySelector 
        onCategorySelect={game.handleCategorySelect} 
        onBack={onBack}
        gameMode="whiching"
      />
    );
  }

  // Deck Selection Screen
  if (game.gameState === WHICHING_STATES.DECK_SELECTION) {
    return (
      <DeckSelector 
        onDeckSelect={game.handleDeckSelect} 
        onBack={game.backToCategorySelection}
        filterCategory={game.selectedCategory}
        gameMode="whiching"
      />
    );
  }

  const isPlaying = game.gameState === WHICHING_STATES.PLAYING;
  const isGameOver = game.gameState === WHICHING_STATES.GAME_OVER;

  // When playing, back button should go to deck selection (category view)
  // When at category/deck selection, back button goes to main menu
  const handleBackDuringGame = () => {
    game.backToDeckSelection();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 p-4 sm:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <WhichingGameHeader
          user={user}
          onLogout={onLogout}
          onViewLeaderboard={onViewLeaderboard}
          onViewStats={onViewStats}
          onNicknameChange={onNicknameChange}
          onBack={handleBackDuringGame}
          deckInfo={game.deckInfo}
          score={game.score}
          strikes={game.strikes}
          maxStrikes={game.maxStrikes}
          consecutiveCorrect={game.consecutiveCorrect}
          showStreakBonus={game.showStreakBonus}
          soundEnabled={game.soundEnabled}
          toggleSound={game.toggleSound}
          gameState={game.gameState}
          isPlaying={isPlaying}
        />

        {/* Challenge Banner */}
        {challengeData && isPlaying && (
          <ChallengeBanner challengeData={challengeData} />
        )}

        {/* Daily Challenge Banner */}
        {game.isDailyChallenge && isPlaying && (
          <div className="mb-4 flex justify-center">
            <div className="bg-gradient-to-r from-orange-600 to-red-600 px-6 py-2 rounded-full flex items-center gap-2 shadow-lg">
              <span className="text-xl">🏆</span>
              <span className="text-white font-bold uppercase tracking-wide">Daily Challenge</span>
              <span className="text-orange-200 text-sm">• Same cards for everyone!</span>
            </div>
          </div>
        )}

        {/* Game Area */}
        {isPlaying && game.currentCards.length === 2 && (
          <div className="mb-4 sm:mb-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 sm:p-8">
              {/* X-Ray Vision Header */}
              {game.xRayVision ? (
                <div className="text-center mb-4 sm:mb-6">
                  <div className="inline-block bg-cyan-600/80 px-4 py-2 rounded-lg animate-pulse">
                    <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center justify-center gap-2">
                      <span className="text-2xl sm:text-3xl">👁️</span>
                      X-Ray Vision
                      <span className="text-2xl sm:text-3xl">👁️</span>
                    </h2>
                    <p className="text-cyan-200 text-sm">Ranks revealed for this matchup!</p>
                  </div>
                </div>
              ) : (
                <h2 className="text-xl sm:text-2xl font-bold text-white text-center mb-4 sm:mb-6">
                  {game.deckInfo?.promptText || 'Which is more popular?'}
                </h2>
              )}
              
              {/* Cards */}
              <div className="flex flex-col sm:flex-row justify-center gap-4 sm:gap-8 items-center relative">
                {game.currentCards.map((card, index) => (
                  <div 
                    key={card.id}
                    className="relative"
                  >
                    <div
                      onClick={() => game.handleCardClick(card)}
                      className={`transform transition-all duration-300 cursor-pointer hover:scale-105 
                        ${game.animatingCard === card.id 
                          ? game.cardResult === 'winner'
                            ? 'scale-110 ring-4 ring-green-500 animate-pulse'
                            : 'scale-90 ring-4 ring-red-500 opacity-75'
                          : ''
                        }`}
                    >
                      <CityCard 
                        card={card} 
                        showRank={game.xRayVision}
                        isWinner={game.animatingCard === card.id && game.cardResult === 'winner'}
                        isLoser={game.animatingCard === card.id && game.cardResult === 'loser'}
                        className="w-36 h-48 sm:w-48 sm:h-64 text-base sm:text-lg"
                        deckColors={game.deckInfo?.colors}
                        isAnimating={game.animatingCard === card.id}
                        animationType={game.animatingCard === card.id ? game.cardResult : null}
                      />
                    </div>
                    {/* Score Pop Animation */}
                    {game.animatingCard === card.id && game.cardResult === 'winner' && (
                      <div className="absolute top-0 left-1/2 transform -translate-x-1/2 animate-bounce text-3xl sm:text-4xl font-bold text-green-400 z-50">
                        +1
                      </div>
                    )}
                    
                    {/* VS indicator between cards on mobile */}
                    {index === 0 && (
                      <div className="sm:hidden text-center my-2">
                        <span className="text-2xl font-bold text-yellow-400">VS</span>
                      </div>
                    )}
                  </div>
                ))}
                
                {/* VS indicator for desktop (between cards) */}
                <div className="hidden sm:block absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
                  <span className="text-3xl font-bold text-yellow-400 drop-shadow-lg">VS</span>
                </div>
              </div>
              
              <p className="text-center text-gray-300 mt-4 sm:mt-6 text-sm sm:text-base">
                Click on the one you think ranks higher!
              </p>
            </div>
          </div>
        )}

        {/* Game Over Screen */}
        {isGameOver && (
          <WhichingGameOver
            isWinner={game.isWinner}
            score={game.score}
            maxWinStreak={game.maxWinStreak}
            maxStrikes={game.maxStrikes}
            challengeData={challengeData}
            user={user}
            scoreSubmitted={game.scoreSubmitted}
            isCreatingChallenge={game.isCreatingChallenge}
            onPlayAgain={game.playAgain}
            onChangeDeck={game.backToDeckSelection}
            onCreateChallenge={game.createChallenge}
            onShareScore={game.shareScore}
            onViewLeaderboard={onViewLeaderboard}
          />
        )}
      </div>
    </div>
  );
};

export default WhichingHourGame;
